<template>
    <csp-desktop-info-card
        v-if="formContext.isLoaded"
        :cols="12"
        :form-context="formContext"
        icon="inticon-users"
        title="Abweichender Versicherungsnehmer"
        @save="savePolicyholderFormData">
        <template v-if="formContext.permissions.canCreateRetroactiveChangesNote === true" v-slot:after-icons>
            <csp-retroactive-changes-modal :record-id="recordId" title="Abweichender Versicherungsnehmer"/>
        </template>
        <div v-if="showChangelogButton" class="float-right">
            <csp-changelog-modal :changelog-url="policyholderChangelogUrl"/>
        </div>
        <h2>Stammdaten</h2>
        <b-row>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                label="Anrede"
                property="gender"
                :required="true"
                type="radio"/>
        </b-row>
        <b-row>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                :label="personLabelPrefix + 'Titel'"
                property="title"
                type="text"/>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                :label="personLabelPrefix + 'Vorname'"
                property="firstName"
                :required="!isGenderCompany"
                type="text"/>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                :label="personLabelPrefix + 'Nachname'"
                property="lastName"
                :required="!isGenderCompany"
                type="text"/>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                label="Firmenname"
                property="companyName"
                :required="isGenderCompany"
                type="text"/>
        </b-row>
        <b-row>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                label="Straße"
                property="street"
                :required="true"
                type="text"/>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                label="PLZ"
                property="postalCode"
                :required="true"
                type="text"/>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                label="Ort"
                property="place"
                :required="true"
                type="text"/>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                label="Land"
                :options-loader="loadCountryOptions"
                property="country"
                type="autocomplete"/>
        </b-row>
        <hr>
        <h2>Kontaktdaten</h2>
        <b-row>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                label="Mobil"
                property="phone"
                type="text"/>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                label="Telefon"
                property="mobile"
                type="text"/>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                label="E-Mail"
                property="email"
                type="text"/>
        </b-row>
        <hr>
        <h2 class="float-left">
            Versicherungsdaten
        </h2>
        <div v-if="showChangelogButton" class="float-right">
            <csp-changelog-modal :changelog-url="policyholderInsuranceChangelogUrl"/>
        </div>
        <div class="clear-float"/>
        <b-row>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                label="Schadensnummer Vers."
                property="adversaryInsuranceReference"
                type="text"/>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                label="Vertragsnummer"
                property="insurancePolicyNr"
                type="text"/>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                label="Teilkasko"
                property="isPartiallyComprehensive"
                type="radio"/>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                label="Vollkasko"
                property="isFullyComprehensive"
                type="radio"/>
        </b-row>
        <b-row>
            <b-col cols="6"/>
            <b-col cols="3">
                <csp-desktop-info-card-element
                    v-if="formContext.form.getProperty('isPartiallyComprehensive') === 'yes'"
                    :key="'copaymentPartial-' + formContext.isInEditMode"
                    :form-context="formContext"
                    label="Selbstbeteiligung Teilkasko"
                    property="partialCoverageCopayment"
                    type="currency"/>
            </b-col>
            <b-col cols="3">
                <csp-desktop-info-card-element
                    v-if="formContext.form.getProperty('isFullyComprehensive') === 'yes'"
                    :key="'copaymentFully-' + formContext.isInEditMode"
                    :form-context="formContext"
                    label="Selbstbeteiligung Vollkasko"
                    property="comprehensiveCopayment"
                    type="currency"/>
            </b-col>
        </b-row>
        <b-row>
            <csp-desktop-info-card-element
                v-show="shouldDisplayInsuranceParent"
                class="col-6"
                :form-context="formContext"
                label="Versicherungskonzern"
                :options-loader="loadInsuranceParentOptions"
                property="insuranceParent"
                type="autocomplete">
                <template v-slot:display-value>
                    <span v-if="formContext.data.insuranceName"><b>{{ formContext.data.insuranceName }}</b><br></span>
                    <span v-if="formContext.data.insurancePhone">{{ formContext.data.insurancePhone }}<br></span>
                    <span v-if="formContext.data.insuranceEmail">
                        <a :href="'mailto:' + formContext.data.insuranceEmail">
                            {{ formContext.data.insuranceEmail }}
                        </a>
                    </span>
                </template>
            </csp-desktop-info-card-element>
            <csp-desktop-info-card-element
                v-show="shouldDisplayInsuranceBranch"
                class="col-6"
                :dependencies="['insuranceParent']"
                :form-context="formContext"
                label="Versicherungsfiliale"
                :options-loader="loadInsuranceBranchOptions"
                property="insuranceBranch"
                type="autocomplete">
                <template v-slot:display-value>
                    <span v-if="formContext.data.insuranceName"><b>{{ formContext.data.insuranceName }}</b><br></span>
                    <span v-if="formContext.data.insurancePhone">{{ formContext.data.insurancePhone }}<br></span>
                    <span v-if="formContext.data.insuranceEmail">
                        <a :href="'mailto:' + formContext.data.insuranceEmail">
                            {{ formContext.data.insuranceEmail }}
                        </a>
                    </span>
                </template>
            </csp-desktop-info-card-element>
        </b-row>
    </csp-desktop-info-card>
</template>

<script>
    import Container from '@services/Container';
    const container = Container.getInstance();
    import FormDataMixin from '../../mixins/FormDataMixin';
    const recordRepository = container.getDesktopRecordRepository();

    export default {
        name: 'ParticipantDifferingPolicyholder',
        mixins: [FormDataMixin],
        computed: {
            policyholderChangelogUrl() {
                return '/api/desktop/records/' + this.recordId + '/participants/policyholder/changelog';
            },
            policyholderInsuranceChangelogUrl() {
                return '/api/desktop/records/' + this.recordId + '/participants/policyholder/insurance/changelog';
            },
            isGenderCompany() {
                return this.formContext.form.getProperty('gender') === 'company';
            },
            personLabelPrefix() {
                if (this.isGenderCompany) {
                    return 'Ansprechpartner ';
                }

                return '';
            },
            recordId() {
                return parseInt(this.$route.params.recordId);
            },
            shouldDisplayInsuranceBranch() {
                if (this.formContext?.isInEditMode) {
                    return true;
                }
                return !!this.formContext?.data['insuranceBranch'];
            },
            shouldDisplayInsuranceParent() {
                if (this.formContext?.isInEditMode) {
                    return true;
                }
                return !this.formContext?.data['insuranceBranch'];
            },
            showChangelogButton() {
                return this.formContext.data.isEntityNew === false
                    && !this.formContext.isInEditMode
                    && this.formContext.permissions.showChangelog === true;
            },
        },
        methods: {
            loadCountryOptions() {
                return recordRepository.loadLegacySelectOptions('countries')
            },
            loadInsuranceBranchOptions() {
                const parentId = this.formContext.form.getProperty('insuranceParent');
                return recordRepository.loadLegacySelectOptions(
                    'childInsurances',
                    {parentInsuranceId: parentId}
                );
            },
            loadInsuranceParentOptions() {
                return recordRepository.loadLegacySelectOptions('parentInsurances');
            },
            savePolicyholderFormData(formContext) {
                const saveFunction = (objectId, postData) => {
                    return recordRepository.saveParticipantsPolicyholder(this.recordId, postData)
                }
                this.saveFormData(formContext, saveFunction, null);
            },
        },
    }
</script>
